<template>
  <div class="container">
  <div class="row p-1 mt-3 bg-white">
    <div class="row p-2">
      <h4 style="margin-left: 20px;"><SettingOutlined />&nbsp;&nbsp;系統設定</h4>
      <a-divider />
      <a-form ref="userConfigForm" :model="formState" v-bind="layout" :rules="formRules" name="basic"
          :label-col="{ span: 7 }" :wrapper-col="{ span: 12 }" :disabled="isUpdatingUserConfig">
        <a-form-item ref="displayName" label="使用者名稱" name="displayName">
          <a-input class="input-text-style" v-model:value="formState.displayName" />
        </a-form-item>
        <a-form-item ref="email" label="管理者信箱" name="email">
          <a-input class="input-text-style" v-model:value="formState.email" />
        </a-form-item>
        <a-form-item label="使用者密碼">
          <UpdatePassword @submit-success="updatePasswordSuccess"/>
        </a-form-item>
        <a-form-item label="PDF 報表印章">
          <a-space direction="vertical">
            <a-upload accept="image/png,image/jpeg" :show-upload-list="false"
                :before-upload="selectStampImage" :disabled="isUpdatingUserConfig || isReadingStamp">
              <a-button :loading="isReadingStamp">選擇印章圖片</a-button>
            </a-upload>
            <span>PNG / JPG，最大 100 KB，尺寸上限 512 × 512。儲存後生效。</span>
            <a-image v-if="stampPreview" :src="stampPreview" :width="160" alt="印章預覽" />
            <a-button v-if="formState.stampImage" danger @click="removeStampImage"
                :disabled="isReadingStamp">移除圖片</a-button>
            <a-space>
              <a-switch v-model:checked="formState.showStampImage"
                  :disabled="!formState.stampImage || isReadingStamp" />
              <span>匯出 PDF 時顯示印章（適用於有印章欄位的報表）</span>
            </a-space>
          </a-space>
        </a-form-item>
        <a-form-item label="問卷抬頭顯示名稱">
          <I18nMessageModal title="多語系設定-問卷抬頭顯示名稱" 
            :message="formState.questionnaireTitle" @update-complete="updateQuestTitleComplete"/>
        </a-form-item>
        <a-form-item label="新增專案-預設值">
          <a-checkbox-group v-model:value="formState.projectConfigs">
            <a-space direction="vertical">
              <a-checkbox value="requireBasic" name="projectConfig" :disabled="formState.projectConfigs.includes('onlyImported') || formState.projectConfigs.includes('onlyImportedJobNo')">基本資料必填&nbsp;<span style="color: red;">(給未提供使用者身分證字號的客戶使用)</span></a-checkbox>
              <a-checkbox value="onlyImported" name="projectConfig" :disabled="formState.projectConfigs.includes('requireBasic') || formState.projectConfigs.includes('disableIdVerify') || formState.projectConfigs.includes('onlyImportedJobNo')">僅允許已匯入人員以證件編號登入&nbsp;<span style="color: red;">(需提供使用者身分證字號)</span></a-checkbox>
              <a-checkbox value="onlyImportedJobNo" name="projectConfig" :disabled="formState.projectConfigs.includes('requireBasic') || formState.projectConfigs.includes('disableIdVerify') || formState.projectConfigs.includes('onlyImported')">僅允許已匯入人員以工號或學號登入&nbsp;<span style="color: red;">(需提供使用者身分證字號)</span></a-checkbox>
              <a-checkbox value="disableIdVerify" name="projectConfig" :disabled="formState.projectConfigs.includes('onlyImported') || formState.projectConfigs.includes('onlyImportedJobNo')">停用證件編號驗證</a-checkbox>
            </a-space>
          </a-checkbox-group>
        </a-form-item>
        <a-form-item label="新增專案-預設問卷類型">
          <a-checkbox-group v-model:value="formState.surveyTypes">
            <a-space direction="vertical">
              <a-checkbox value="OVER_WORK" v-if="authorizedQuestTypes.includes('OVER_WORK')" name="surveyType" :disabled="formState.surveyTypes.includes('OVER_WORK_MIND')">過勞評估</a-checkbox>
              <a-checkbox value="OVER_WORK_MIND" v-if="authorizedQuestTypes.includes('OVER_WORK_MIND')" name="surveyType" :disabled="formState.surveyTypes.includes('OVER_WORK')">過勞與心理評估</a-checkbox>
              <a-checkbox value="BSRS5" v-if="authorizedQuestTypes.includes('BSRS5')" name="surveyType">心情溫度計(簡式健康量表)</a-checkbox>
              <a-checkbox value="MUSCUL" v-if="authorizedQuestTypes.includes('MUSCUL')" name="surveyType">肌肉骨骼自覺症狀評量表</a-checkbox>
              <a-checkbox value="OVER_LOAD" v-if="authorizedQuestTypes.includes('OVER_LOAD')" name="surveyType">過負荷問卷</a-checkbox>
              <a-checkbox value="GENERAL_PHYSICAL" v-if="authorizedQuestTypes.includes('GENERAL_PHYSICAL')" name="surveyType">勞工一般體檢</a-checkbox>
              <a-checkbox value="ELDERLY_WORK_ADAPTABILITY" v-if="authorizedQuestTypes.includes('ELDERLY_WORK_ADAPTABILITY')" name="surveyType">中高齡工作適能評估&nbsp;<span style="color: red;">(僅開放給未匯入與45歲以上人員填寫)</span></a-checkbox>
              <a-checkbox value="WORKPLACE_UNLAWFUL_ACTS" v-if="authorizedQuestTypes.includes('WORKPLACE_UNLAWFUL_ACTS')" name="surveyType">職場不法侵害風險評估</a-checkbox>
              <a-checkbox value="SELF_APPRAISAL_NUTRITION" v-if="authorizedQuestTypes.includes('SELF_APPRAISAL_NUTRITION')" name="surveyType">自我評核-營養</a-checkbox>
            </a-space>
          </a-checkbox-group>
        </a-form-item>
        <a-form-item label="頁面預設查詢區間">
          <a-dropdown style="float: right;">
            <template #overlay>
              <a-menu @click="updateQueryDuration">
                <a-menu-item key="LastThreeMonth">
                  <CalendarTwoTone />&nbsp;三個月內 - 新增之專案
                </a-menu-item>
                <a-menu-item key="LastSixMonth">
                  <CalendarTwoTone />&nbsp;六個月內 - 新增之專案
                </a-menu-item>
                <a-menu-item key="LastYear">
                  <CalendarTwoTone />&nbsp;一年內 - 新增之專案
                </a-menu-item>
                <a-menu-item key="LastTwoYear">
                  <CalendarTwoTone />&nbsp;兩年內 - 新增之專案
                </a-menu-item>
                <a-menu-item key="AllYears">
                  <CalendarTwoTone />&nbsp;所有已新增之專案
                </a-menu-item>
              </a-menu>
            </template>
            <a-button>
              <CalendarTwoTone />{{ ProjectQueryDurationNames.get(formState.queryDuration) }}&nbsp;<DownOutlined />
            </a-button>
          </a-dropdown>
        </a-form-item>
        <a-form-item :wrapper-col="{ span: 14, offset: 8 }">
          <a-space>
            <a-button type="default" @click="resetUserConfig" :disabled="isReadingStamp">重置設定</a-button>
            <a-button type="primary" @click="saveUserConfig" :disabled="isReadingStamp">儲存</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </div>
  </div>
  </div>
</template>
<script setup>
import { SettingOutlined, CalendarTwoTone, DownOutlined } from "@ant-design/icons-vue";
import { computed, reactive, onMounted, ref } from 'vue';

import { message } from 'ant-design-vue';

import { ProjectQueryDurationNames } from "@/common";
import common from "@/common";
import config from "@/config";

import I18nMessageModal from '../common/I18nMessageModal.vue';
import UpdatePassword from './UpdatePassword.vue';

const layout = {
  labelCol: {
    span: 4,
  },
  wrapperCol: {
    span: 16,
  },
};

const formRules = {
  displayName: [{
    required: true,
    message: '請輸入管理者名稱',
    trigger: ['blur']
  }], 
  email: [{
    type: 'email',
    message: '電子郵件地址格式有誤',
    trigger: ['blur']
  }, {
    required: true,
    message: '請輸入管理者電子郵件地址',
    trigger: ['blur']
  }]
};

const userConfigForm = ref();
const authorizedQuestTypes = ref([]);
const isUpdatingUserConfig = ref(false);
const isReadingStamp = ref(false);
const sourceFormState = ref({});
const formState = reactive({
  stampImage: null,
  showStampImage: false,
  displayName: '',
  email: '',
  questionnaireTitle: {},
  projectConfigs: [],
  surveyTypes: [],
  queryDuration: 'LastThreeMonth'
});

const stampPreview = computed(() => formState.stampImage
  ? `data:image/${formState.stampImage.startsWith('iVBOR') ? 'png' : 'jpeg'};base64,${formState.stampImage}`
  : '');

const selectStampImage = async (file) => {
  if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > 100 * 1024) {
    message.error('請選擇 100 KB 以下的 PNG 或 JPG 圖片');
    return false;
  }
  isReadingStamp.value = true;
  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => image.width <= 512 && image.height <= 512 ? resolve() : reject();
      image.onerror = reject;
      image.src = dataUrl;
    });
    formState.stampImage = dataUrl.split(',')[1];
  } catch {
    message.error('無法讀取圖片，請確認為有效圖片且尺寸不超過 512 × 512');
  } finally {
    isReadingStamp.value = false;
  }
  return false; // Save bytes together with the settings; never upload automatically.
};

const removeStampImage = () => {
  formState.stampImage = null;
  formState.showStampImage = false;
};

const updateQueryDuration = (menuItem) => {
    if (formState.queryDuration === menuItem.key) {
        return ;
    }
    formState.queryDuration = menuItem.key;
}

const updateUserConfig = (userConfig) => {
  userConfig = JSON.parse(JSON.stringify(userConfig));
  formState.stampImage = userConfig.stampImage || null;
  formState.showStampImage = userConfig.showStampImage === true && !!formState.stampImage;
  formState.displayName = userConfig.displayName;
  formState.email = userConfig.email;
  formState.questionnaireTitle = userConfig.questionnaireTitle;
  formState.projectConfigs = userConfig.projectConfigs;
  formState.surveyTypes = userConfig.surveyTypes;
  formState.queryDuration = userConfig.queryDuration;
}

const resetUserConfig = () => {
  if (userConfigForm.value != null) {
    userConfigForm.value.resetFields();
  }
  updateUserConfig(sourceFormState.value);
}

const saveUserConfig = () => {
  userConfigForm.value.validate()
  .then(() => {
    isUpdatingUserConfig.value = true;
    common.postRequest(config.mgmtUserEntity, formState,
      () => {
        isUpdatingUserConfig.value = false;
        sourceFormState.value = JSON.parse(JSON.stringify(formState));
        message.success("系統設定儲存完畢！");
      }, () => {
        isUpdatingUserConfig.value = false;
      });
  }).catch(() => { });
}

const updatePasswordSuccess = () => {
  message.success("密碼變更完成！");
}

const updateQuestTitleComplete = (questTitleMessage) => {
  formState.questionnaireTitle.messageZhTw = questTitleMessage.messageZhTw;
  formState.questionnaireTitle.messageEnUs = questTitleMessage.messageEnUs;
  formState.questionnaireTitle.messageThTh = questTitleMessage.messageThTh;
  formState.questionnaireTitle.messageViVn = questTitleMessage.messageViVn;
  formState.questionnaireTitle.messageIdId = questTitleMessage.messageIdId;
  formState.questionnaireTitle.messageJaJp = questTitleMessage.messageJaJp;
}

onMounted(() => {
  common.getRequest(config.mgmtUserEntity, (response) => {
    sourceFormState.value = response.data.userConfig;
    authorizedQuestTypes.value = response.data.authorizedQuestTypes;
    updateUserConfig(response.data.userConfig);
  }, (error) => {
    console.error("Get Mgmt User entity failed: " + error);
    authorizedQuestTypes.value = [];
  });
});

</script>
<style scoped>
  button,
  span {
    display: inline-flex;
    align-items: center;
  }
  .input-text-style {
    width: 200px;
  }
</style>
